import { useState, useEffect, useCallback } from 'react';
import {
  recentlyViewedService,
  RecentlyViewedStock,
} from '@services/recentlyViewed/recentlyViewedService';
import { useFocusEffect } from '@react-navigation/native';

interface UseRecentlyViewedReturn {
  recentlyViewed: RecentlyViewedStock[];
  isLoading: boolean;
  addRecentlyViewed: (ticker: string, companyName: string) => Promise<void>;
  removeRecentlyViewed: (ticker: string) => Promise<void>;
  clearRecentlyViewed: () => Promise<void>;
  refreshRecentlyViewed: () => Promise<void>;
}

export const useRecentlyViewed = (): UseRecentlyViewedReturn => {
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedStock[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch recently viewed
  const fetchRecentlyViewed = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await recentlyViewedService.getRecentlyViewed();
      setRecentlyViewed(data);
    } catch (err) {
      console.error('useRecentlyViewed fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Add to recently viewed
  const addRecentlyViewed = useCallback(
    async (ticker: string, companyName: string) => {
      await recentlyViewedService.addRecentlyViewed(ticker, companyName);
      await fetchRecentlyViewed();
    },
    [fetchRecentlyViewed]
  );

  // Remove from recently viewed
  const removeRecentlyViewed = useCallback(
    async (ticker: string) => {
      await recentlyViewedService.removeRecentlyViewed(ticker);
      await fetchRecentlyViewed();
    },
    [fetchRecentlyViewed]
  );

  // Clear all recently viewed
  const clearRecentlyViewed = useCallback(async () => {
    await recentlyViewedService.clearRecentlyViewed();
    setRecentlyViewed([]);
  }, []);

  // Refresh recently viewed
  const refreshRecentlyViewed = useCallback(async () => {
    await fetchRecentlyViewed();
  }, [fetchRecentlyViewed]);

  // Initial fetch on mount
  useEffect(() => {
    fetchRecentlyViewed();
  }, [fetchRecentlyViewed]);

  // Refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchRecentlyViewed();
    }, [fetchRecentlyViewed])
  );

  return {
    recentlyViewed,
    isLoading,
    addRecentlyViewed,
    removeRecentlyViewed,
    clearRecentlyViewed,
    refreshRecentlyViewed,
  };
};
